import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useRequiredScopeParam, useScopeParam } from '../../hooks/useScopeParam'
import { getNetworkNames, Network } from '../../../types/network'
import { useIsApiReachable, useRuntimeFreshness } from './hook'
import { getNameForScope } from '../../../types/searchScope'
import { exhaustedTypeWarning } from '../../../types/errors'
import { StickyAlert } from '../StickyAlert'

export const NetworkOfflineBanner: FC<{ wantedNetwork?: Network }> = ({ wantedNetwork }) => {
  const scope = useScopeParam()
  const { t } = useTranslation()
  const targetNetwork = wantedNetwork || scope?.network || Network.mainnet
  const isNetworkReachable = useIsApiReachable(targetNetwork)
  const networkNames = getNetworkNames(t)
  const target = networkNames[targetNetwork]
  if (!isNetworkReachable.reachable) {
    if (isNetworkReachable.reason === 'userOffline') {
      return <StickyAlert severity="warning">{t('home.userOffline', { target })}</StickyAlert>
    }
    if (isNetworkReachable.reason === 'apiOffline') {
      return <StickyAlert severity="warning">{t('home.apiOffline', { target })}</StickyAlert>
    }
    exhaustedTypeWarning('Unexpected isNetworkReachable reason', isNetworkReachable.reason)
  }
  return null
}

export const RuntimeOfflineBanner: FC = () => {
  const scope = useRequiredScopeParam()
  const { t } = useTranslation()

  const { outOfDate, lastUpdate } = useRuntimeFreshness(scope)
  if (!outOfDate) return null
  const target = getNameForScope(t, scope)
  return (
    <StickyAlert severity="warning">
      {lastUpdate
        ? t('home.runtimeOutOfDateSince', { target, lastUpdate })
        : t('home.runtimeOutOfDate', { target })}
    </StickyAlert>
  )
}
